export type Locale = "en" | "fr" | "ar";

export type TranslationDict = Record<string, string>;

export const translations: Record<Locale, TranslationDict> = {
  en: {

    //INTAKE ONLY
    "common.next": "Next",
    "common.back": "Back",
    "common.strongly": "Strongly",
    "common.agree": "agree",
    "common.disagree": "disagree",
    "common.stronglyDisagree": "Strongly|disagree",
    "common.stronglyAgree": "Strongly|agree",
    "common.selectAll": "Select all that apply.",
    "common.notImportant": "Not important",
    "common.veryImportant": "Very important",
    "common.continue": "Continue",
    "common.finish": "Finish",
    "common.yes": "Yes",
    "common.no": "No",
    "common.male": "Male",
    "common.female": "Female",

    "inPerson.yes": "Yes",
    "inPerson.maybe": "Maybe",
    "inPerson.notNow": "Not right now",

    "lang.en": "English",
    "lang.fr": "French",
    "lang.ar": "Arabic",
    "lang.ur": "Urdu",
 
    // SECTIONS
    "intake.sections.gettingStarted": "Getting Started",
    "intake.sections.whoYoureHelping": "Who You’re Helping",
    "intake.sections.basics": "The Basics",
    "intake.sections.language": "Language & Communication",
    "intake.sections.lifestyle": "Lifestyle & Energy",
    "intake.sections.relationship": "Relationship Style",
    "intake.sections.family": "Marriage & Family",
    "intake.sections.faith": "Faith & Values",
    "intake.sections.openness": "Openness & Logistics",
    "intake.sections.preferences": "Preferences",
    "intake.sections.introductions": "Introductions",
    "intake.sections.spousePreferences": "Spouse Preferences",
    "intake.sections.contact": "Contact",
    "intake.sections.finalReflection": "Final Reflection — Your Intention",

    // GETTING STARTED
    "intake.gettingStarted.q_forWho": "Who is this experience for?",
    "intake.gettingStarted.forMe": "Me",
    "intake.gettingStarted.forSomeoneElse": "I’m helping match someone else",
    "intake.gettingStarted.q_role": "What’s your role?",
    "intake.gettingStarted.guardianNote": "The match seeker will approve every introduction.",

    // ROLES (used in guardian + intros)
    "intake.roles.parent": "Parent",
    "intake.roles.wali": "Wali",
    "intake.roles.sibling": "Sibling",
    "intake.roles.friend": "Friend",
    "intake.roles.other": "Other",

    // WHO YOU’RE HELPING
    "intake.whoHelping.firstName": "What’s their first name?",
    "intake.whoHelping.age": "How old are they?",
    "intake.whoHelping.gender": "What’s their gender?",
    "intake.whoHelping.location": "Where are they located?",

    // BASICS
    "intake.basics.firstName": "What's your first name?",
    "intake.basics.age": "How old are you?",
    "intake.basics.gender": "What's your gender?",
    "intake.basics.location": "Where are you located?",
    "intake.basics.inPerson": "Are you open to in-person events?",

    // LANGUAGE
    "intake.language.guardianLabel": "I'm looking for someone who speaks…",
    "intake.language.spokenLabel": "What languages are you comfortable speaking in a relationship?",
    "intake.language.preferredLabel": "What language(s) would you prefer to use with a partner? (optional)",
    "intake.language.other": "Other languages (optional)",
    "intake.language.helper": "This helps us avoid awkward communication mismatches.",

    // LIFESTYLE
    "intake.lifestyle.q_activity": "If I do nothing for an entire day, I feel…",
    "intake.lifestyle.min_restless": "Restless",
    "intake.lifestyle.max_content": "Perfectly content",
    "intake.lifestyle.q_travelAnxious": "Traveling makes me anxious",
    "intake.lifestyle.q_vacation": "When I go on vacation, I'd prefer…",
    "intake.lifestyle.min_relaxing": "Relaxing|all day",
    "intake.lifestyle.max_fullSchedule": "A full|schedule",
    "intake.lifestyle.q_exercise": "Exercising is an important part of my lifestyle",
    "intake.lifestyle.q_diet": "I pay attention to what I eat",

    // RELATIONSHIP
    "intake.relationship.q_romance": "I enjoy grand gestures of romance",
    "intake.relationship.q_conflict": "I prefer to resolve conflict immediately rather than wait",
    "intake.relationship.q_processing": "I need time to process before sharing what's bothering me",
    "intake.relationship.q_similarity": "I want my partner to be similar to me",
    "intake.relationship.q_openness": "I'm comfortable discussing difficult topics openly",

    // FAMILY
    "intake.family.q_marriageYoung": "Getting married young is a mistake",
    "intake.family.q_prevMarried_guardian": "Has the person you are helping been married before?",
    "intake.family.q_prevMarried_candidate": "Have you been previously married?",
    "intake.family.q_hasChildren": "Do you currently have children?",
    "intake.family.q_childrenLivingWith": "Do your children live with you?",
    "intake.family.someOfTheTime": "Some of the time",
    "intake.family.q_openMoreChildren": "Are you open to having more children?",
    "intake.family.unsure": "Unsure",
    "intake.family.q_childrenCount": "How many children would you like?",
    "intake.family.q_finances": "Finances should not delay starting a family",
    "intake.family.q_inlaws": "It's okay if my spouse's family lives with us",
    "intake.family.q_parentsDecisions": "I run major decisions by my parents",

    // FAITH
    "intake.faith.q_role": "Religion plays a significant role in my life",
    "intake.faith.q_denomImportance": "Having a shared Islamic denomination (e.g., Sunni, Shia, etc.) is important to me",
    "intake.faith.q_denomWhich": "Which denomination do you identify with? (Optional)",
    "intake.faith.denom.none": "No specific denomination",
    "intake.faith.denom.sunni": "Sunni",
    "intake.faith.denom.shia": "Shia",
    "intake.faith.denom.sufi": "Sufi",
    "intake.faith.denom.salafi": "Salafi",
    "intake.faith.denom.preferNot": "Prefer not to specify",
    "intake.faith.denom.other": "Other",
    "intake.faith.denom.otherLabel": "Other denomination (optional)",
    "intake.faith.denom.otherPlaceholder": "Type here…",
    "intake.faith.q_halalFood": "I only eat halal food",
    "intake.faith.q_ramadan": "I fast every Ramadan",
    "intake.faith.q_halalIncome": "I make a big effort to maintain halal income",
    "intake.faith.q_childrenReligious": "It's important my children are raised religious",

    // PLACEHOLDERS / ERRORS
    "intake.placeholders.firstName": "e.g. Sarah",
    "intake.placeholders.age": "e.g. 28",
    "intake.placeholders.cityShort": "e.g. Toronto, ON",
    "intake.placeholders.cityLong": "e.g. Toronto, ON or Montreal, QC",
    "intake.placeholders.otherLanguages": "e.g. Spanish, German",
    "intake.placeholders.email": "e.g. sara@gmail.com",
    "intake.placeholders.phone": "e.g. +1 416 555 0123",

    "intake.errors.age18": "Must be 18 or older",
    "intake.errors.emailInvalid": "Please enter a valid email.",

    "intake.preferences.ageOpenTo": "What age range(s) are you open to?",
    "intake.preferences.agePreferred": "Preferred age range(s):",

    "intake.introductions.q1": "Are you open to third-party match introductions (e.g., from the match’s parent/guardian/friend)?",
    "intake.introductions.q2": "If yes, who are you comfortable with?",

    "intake.spouse.introTitle": "How important are the following in a match?",
    "intake.spouse.introBody": "For each statement, rate how important it is for a match. Your answers help us find the best overall fit.",
    "intake.spouse.q_faith_role": "A strong commitment to daily religious practice",
    "intake.spouse.q_faith_ramadan": "Regular fasting during Ramadan",
    "intake.spouse.q_faith_halal_food": "Strictly eating halal food",
    "intake.spouse.q_faith_halal_income": "Earning halal income",
    "intake.spouse.q_fam_inlaws": "Comfort living in a family-oriented household (e.g., near or with parents)",
    "intake.spouse.q_fam_parents": "Involving parents in major life decisions",
    "intake.spouse.q_fam_finances": "Starting a family only after financial stability",
    "intake.spouse.q_rel_conflict": "Addressing conflict sooner rather than later",
    "intake.spouse.q_rel_openness": "Comfort discussing difficult topics openly",
    "intake.spouse.q_rel_processing": "Taking time to process emotions before discussing them",
    "intake.spouse.q_lifestyle_travel": "Enjoying travel and new experiences",
    "intake.spouse.q_lifestyle_exercise": "Prioritizing health and exercise",

    "intake.contact.title": "How can we reach you?",
    "intake.contact.email": "Email address",
    "intake.contact.phone": "Phone number (optional)",
    "intake.contact.helper": "We’ll use your email to confirm your record and send you match details, and only share it with your match.",

    // PHOTO STEP
    "intake.sections.photo": "Almost There",

    "intake.photo.title": "Add a photo?",
    "intake.photo.descriptionBeforeEmphasis": "Totally optional. If you do, it will",
    "intake.photo.descriptionEmphasis": "only",
    "intake.photo.descriptionAfterEmphasis": "be shown to someone you're matched with — never publicly, never to anyone else.",
    "intake.photo.previewAlt": "Your photo",
    "intake.photo.remove": "Remove",
    "intake.photo.tapToUpload": "Tap to upload",
    "intake.photo.privacyNote": "Private and encrypted — only visible to your match.",
    "intake.photo.helper": "You can always skip this and add one later.",

    "intake.reflection.labelCandidate": "One thing that's important for someone to understand about me (my values, goals, or mindset around marriage) is…",
    "intake.reflection.labelGuardian": "From your perspective, what matters most in a match?",
    "intake.reflection.placeholder": "Share whatever comes to mind...",
    "intake.reflection.helper": "This is optional, but helps us understand you better.",

    "intake.openness.abroad": "It's important my children spend time living abroad",
    "intake.openness.childrenLang": "My children should learn more than one language",
    "intake.openness.longDistance": "I'm open to a long-distance relationship",
    "intake.openness.financialRisk": "I'm comfortable with financial risk",
    "intake.openness.financialSupport": "I'd be comfortable being financially supported by my partner",

    // WELCOME STEP
    "welcome.title.prefix": "Let's start with",
    "welcome.title.highlight": "intention.",
    "welcome.title.suffix": "",
    "welcome.body.line1": "This takes about 5–7 minutes.",
    "welcome.body.line2": "There are no right or wrong answers.",
    "welcome.body.line3a": "Matching is based on alignment —",
    "welcome.body.line3b": "not perfection.",
    "welcome.cta": "Continue",

    // AGE PREFERENCES STEP
    "agePrefs.helper": "Select all age ranges you're open to. This helps us show you compatible matches.",

    // BASICS STEP
    "basics.helper": "Tell us a little about yourself.", 
    "basics.firstName": "First Name", 
    "basics.firstName.placeholder": "e.g. Sarah", 
    "basics.age": "Age", 
    "basics.age.placeholder": "e.g. 28", 
    "basics.gender": "Gender", 
    "basics.city": "City/Region", 
    "basics.city.placeholder": "e.g. Toronto, ON or Montreal, QC", 
    "basics.inPerson": "Open to in-person events?", 
    "gender.male": "Male", 
    "gender.female": "Female", 
    "validation.age18": "Must be 18 or older",

    // COMPLETION STEP
    "completion.title": "All done.",
    "completion.body": "Your profile has been created.",
    "completion.cta": "Continue",

    // SUCCESS STEP
    "success.title": "You're all set.",
    "success.body": "Your spot is confirmed. We'll email you when matching begins.",
    "success.card.planLabel": "Plan",
    "success.card.statusLabel": "Status",
    "success.card.trialEndsLabel": "Trial Ends",
    "success.cta": "Go to homepage",
    "success.footer": "Check your email for next steps and updates.",

    // CANCEL STEP
    "cancel.title": "No worries.",
    "cancel.body": "Your progress is saved. You can return anytime to complete your payment.",
    "cancel.ctaReturn": "Return to payment",
    "cancel.ctaHome": "Go to homepage",
    "cancel.footer": "We've saved your answers. Pick up right where you left off.",

    // FAITH VALUES STEP
    "steps.faith.title": "Faith & Values",
    "steps.faith.subtitle": "Everyone practices differently. Answer honestly.",

    "intake.faith.role": "Religion plays a significant role in my life",
    "intake.faith.children": "It's important my children are raised religious",
    "intake.faith.halalFood": "I only eat halal food",
    "intake.faith.ramadan": "I fast every Ramadan",
    "intake.faith.halalIncome": "I go to great lengths to maintain halal income",

    // LANGUAGE STEP
    "steps.language.title": "Language & Communication",
    "steps.language.subtitle": "This helps us avoid awkward communication mismatches.",
    "steps.language.spoken": "Languages you're comfortable speaking in a relationship",
    "steps.language.other": "Other languages (optional)",
    "steps.language.otherPlaceholder": "e.g. Spanish, German",
    "steps.language.preferred": "Language(s) you'd prefer to use with a partner (optional)",

    // LIFESTYLE STEP
    "steps.lifestyle.title": "Lifestyle & Energy",
    "steps.lifestyle.subtitle": "How do you like to spend your time?",

    "intake.lifestyle.activity.q": "If I do nothing for an entire day, I feel…",
    "intake.lifestyle.activity.min": "Restless",
    "intake.lifestyle.activity.max": "Perfectly content",

    "intake.lifestyle.travel.q": "Traveling makes me anxious",

    "intake.lifestyle.vacation.q": "When I go on vacation, I'd prefer…",
    "intake.lifestyle.vacation.min": "Relaxing|all day",
    "intake.lifestyle.vacation.max": "A full|schedule",

    "intake.lifestyle.exercise.q": "Exercising is an important part of my lifestyle",
    "intake.lifestyle.diet.q": "I pay attention to what I eat",

    // MARRIAGE FAMILY STEP
    "steps.marriageFamily.title": "Marriage & Family",
    "steps.marriageFamily.subtitle": "Your vision for the future.",

    "intake.marriageAge.q": "Getting married young is a mistake",
    "intake.childrenCount.label": "I want a family with… (number of children)",
    "intake.famFinances.q": "Finances should not delay starting a family",
    "intake.famInlaws.q": "It's okay if my spouse's family lives with us",
    "intake.famParentsDecisions.q": "I run major decisions by my parents",

    // OPENNESS STEP
    "steps.openness.title": "Openness & Logistics",
    "steps.openness.subtitle": "Practical considerations for your future.",

    // --- PAYMENT ---
    "payment.title": "Choose your level of involvement",
    "payment.subtitle": "You're now part of Niyyah. Select the option that fits your pace.",

    "payment.trial.title": "Each plan starts with a 60-day free trial.",
    "payment.trial.subtitle": "You won't be charged until it ends. Cancel anytime.",

    "payment.error.missingEmail": "Missing email. Please go back and enter your email.",
    "payment.error.failed": "Payment failed. Please try again.",
    "payment.backToIntake": "Back to intake",

    "payment.mostPopular": "Most Popular",
    "payment.redirecting": "Redirecting...",

    "payment.badge.private": "Private. No public profiles.",
    "payment.badge.flexible": "You can upgrade or cancel anytime.",
    "payment.footer": "Matching is based on alignment and availability. Quality over quantity.",

    // Plan: Community
    "payment.plan.community.title": "Basic Enrollment",
    "payment.plan.community.price": "$10",
    "payment.plan.community.period": "per month (after free trial)",
    "payment.plan.community.cta": "Enroll now",
    "payment.plan.community.f1": "Join the Niyyah community",
    "payment.plan.community.f2": "Consideration for invite-only events",
    "payment.plan.community.f3": "Eligibility for future matching",
    "payment.plan.community.f4": "Private, values-based profile",

    // Plan: Matching
    "payment.plan.matching.title": "Active Matching",
    "payment.plan.matching.price": "$20",
    "payment.plan.matching.period": "per month (after free trial)",
    "payment.plan.matching.cta": "Active matching",
    "payment.plan.matching.f1": "Everything in Basic Enrollment",
    "payment.plan.matching.f2": "Ongoing private match consideration",
    "payment.plan.matching.f3": "Periodic match introductions",
    "payment.plan.matching.f4": "Priority over passive members",

    // --- LEGACY STEPS (if you keep these files around) ---
    "legacy.reflection.title": "Final Reflection",
    "legacy.reflection.helper": "This is optional, but helps us understand you better.",
    "legacy.reflection.label": "One thing that's important for someone to understand about me is…",
    "legacy.reflection.placeholder": "Share whatever comes to mind...",

    "legacy.relationship.title": "Relationship Style",
    "legacy.relationship.subtitle": "How do you relate to others?",
    "legacy.relationship.q_romance": "I enjoy grand gestures of romance",
    "legacy.relationship.q_conflict": "I prefer to resolve conflict immediately rather than wait",
    "legacy.relationship.q_processing": "I need time to process before sharing what's bothering me",
    "legacy.relationship.q_similarity": "I want my partner to be similar to me",
    "legacy.relationship.q_openness": "I'm comfortable discussing difficult topics openly",

    // CHECKOUT SUCCESS PAGE
    "checkoutSuccess.title": "You're in.",
    "checkoutSuccess.subtitleLine1": "You're now part of the beta.",
    "checkoutSuccess.subtitleLine2": "We'll email you when there's a match.",

    "checkoutSuccess.statusLabel": "Status",
    "checkoutSuccess.statusValue": "Beta Access",

    "checkoutSuccess.nextLabel": "What's Next",
    "checkoutSuccess.nextBody": "We'll notify you by email when we find a potential match",

    "checkoutSuccess.inviteTitle": "Niyyah grows best through trusted circles.",
    "checkoutSuccess.inviteBody": "If you know someone seeking marriage for themselves — or on behalf of someone else — please invite them privately.",

    "checkoutSuccess.shareText": "A thoughtful, private way to meet.",
    "checkoutSuccess.share": "Share",
    "checkoutSuccess.copyLink": "Copy link",
    "checkoutSuccess.copied": "Copied ✓",

    "checkoutSuccess.backHome": "Back to home",

    "checkoutSuccess.footer": "Check your email for confirmation and next steps.",
    "checkoutSuccess.sessionLabel": "Session",

  },

  fr: {

    // INTAKE ONLY
    "common.next": "Suivant",
    "common.back": "Retour",
    "common.strongly": "Fortement",
    "common.agree": "d’accord",
    "common.disagree": "en désaccord",
    "common.stronglyDisagree": "Tout à fait|en désaccord",
    "common.stronglyAgree": "Tout à fait |d’accord", 
    "common.selectAll": "Sélectionnez toutes les réponses applicables.",
    "common.notImportant": "Pas important",
    "common.veryImportant": "Très important",
    "common.continue": "Continuer",
    "common.finish": "Terminer",
    "common.yes": "Oui",
    "common.no": "Non",
    "common.male": "Homme",
    "common.female": "Femme",


    "inPerson.yes": "Oui",
    "inPerson.maybe": "Peut-être",
    "inPerson.notNow": "Pas pour le moment",

    "lang.en": "Anglais",
    "lang.fr": "Français",
    "lang.ar": "Arabe",
    "lang.ur": "Ourdou",

    // SECTIONS
    "intake.sections.gettingStarted": "Pour commencer",
    "intake.sections.whoYoureHelping": "La personne que vous aidez",
    "intake.sections.basics": "Les informations de base",
    "intake.sections.language": "Langue et communication",
    "intake.sections.lifestyle": "Style de vie et énergie",
    "intake.sections.relationship": "Style relationnel",
    "intake.sections.family": "Mariage et famille",
    "intake.sections.faith": "Foi et valeurs",

    // GETTING STARTED
    "intake.gettingStarted.q_forWho": "Cette expérience est pour qui ?",
    "intake.gettingStarted.forMe": "Moi",
    "intake.gettingStarted.forSomeoneElse": "J’aide quelqu’un à trouver un(e) partenaire",
    "intake.gettingStarted.q_role": "Quel est votre rôle ?",
    "intake.gettingStarted.guardianNote": "La personne concernée approuvera chaque mise en relation.",

    // ROLES
    "intake.roles.parent": "Parent",
    "intake.roles.wali": "Wali",
    "intake.roles.sibling": "Frère/Sœur",
    "intake.roles.friend": "Ami(e)",
    "intake.roles.other": "Autre",

    // WHO YOU’RE HELPING
    "intake.whoHelping.firstName": "Quel est son prénom ?",
    "intake.whoHelping.age": "Quel âge a-t-il/elle ?",
    "intake.whoHelping.gender": "Quel est son genre ?",
    "intake.whoHelping.location": "Où se trouve-t-il/elle ?",

    // BASICS
    "intake.basics.firstName": "Quel est votre prénom ?",
    "intake.basics.age": "Quel âge avez-vous ?",
    "intake.basics.gender": "Quel est votre genre ?",
    "intake.basics.location": "Où êtes-vous situé(e) ?",
    "intake.basics.inPerson": "Êtes-vous ouvert(e) aux événements en personne ?",

    // LANGUAGE
    "intake.language.guardianLabel": "Je recherche quelqu’un qui parle…",
    "intake.language.spokenLabel": "Quelles langues parlez-vous aisément dans une relation ?",
    "intake.language.preferredLabel": "Quelle(s) langue(s) préférez-vous utiliser avec un(e) partenaire ? (optionnel)",
    "intake.language.other": "Autres langues (optionnel)",
    "intake.language.helper": "Cela nous aide à éviter les malentendus de communication.",

    // LIFESTYLE
    "intake.lifestyle.q_activity": "Si je ne fais rien pendant toute une journée, je me sens…",
    "intake.lifestyle.min_restless": "Agité(e)",
    "intake.lifestyle.max_content": "Parfaitement bien",
    "intake.lifestyle.q_travelAnxious": "Voyager me rend anxieux(se)",
    "intake.lifestyle.q_vacation": "Quand je pars en vacances, je préfère…",
    "intake.lifestyle.min_relaxing": "Me détendre|toute la journée",
    "intake.lifestyle.max_fullSchedule": "Un programme|bien rempli",
    "intake.lifestyle.q_exercise": "L’exercice est important dans mon mode de vie",
    "intake.lifestyle.q_diet": "Je fais attention à ce que je mange",

    // RELATIONSHIP
    "intake.relationship.q_romance": "J’apprécie les grands gestes romantiques",
    "intake.relationship.q_conflict": "Je préfère résoudre les conflits immédiatement plutôt que d’attendre",
    "intake.relationship.q_processing": "J’ai besoin de temps pour réfléchir avant de partager ce qui me dérange",
    "intake.relationship.q_similarity": "Je souhaite que mon/ma partenaire me ressemble",
    "intake.relationship.q_openness": "Je suis à l’aise pour discuter ouvertement de sujets difficiles",

    // FAMILY
    "intake.family.q_marriageYoung": "Se marier jeune est une erreur",
    "intake.family.q_prevMarried_guardian": "La personne que vous aidez a-t-elle déjà été mariée ?",
    "intake.family.q_prevMarried_candidate": "Avez-vous déjà été marié(e) ?",
    "intake.family.q_hasChildren": "Avez-vous actuellement des enfants ?",
    "intake.family.q_childrenLivingWith": "Vos enfants vivent-ils avec vous ?",
    "intake.family.someOfTheTime": "Une partie du temps",
    "intake.family.q_openMoreChildren": "Êtes-vous ouvert(e) à avoir d’autres enfants ?",
    "intake.family.unsure": "Incertain(e)",
    "intake.family.q_childrenCount": "Combien d’enfants souhaiteriez-vous ?",
    "intake.family.q_finances": "Les finances ne devraient pas retarder la création d’une famille",
    "intake.family.q_inlaws": "Cela ne me dérange pas que la famille de mon/ma conjoint(e) vive avec nous",
    "intake.family.q_parentsDecisions": "Je consulte mes parents pour les décisions importantes",

    // FAITH
    "intake.faith.q_role": "La religion joue un rôle important dans ma vie",
    "intake.faith.q_denomImportance": "Avoir une dénomination islamique commune (ex. sunnite, chiite, etc.) est important pour moi",
    "intake.faith.q_denomWhich": "À quelle dénomination vous identifiez-vous ? (Optionnel)",
    "intake.faith.denom.none": "Aucune dénomination spécifique",
    "intake.faith.denom.sunni": "Sunnite",
    "intake.faith.denom.shia": "Chiite",
    "intake.faith.denom.sufi": "Soufi",
    "intake.faith.denom.salafi": "Salafi",
    "intake.faith.denom.preferNot": "Préfère ne pas préciser",
    "intake.faith.denom.other": "Autre",
    "intake.faith.denom.otherLabel": "Autre dénomination (optionnel)",
    "intake.faith.denom.otherPlaceholder": "Écrivez ici…",
    "intake.faith.q_halalFood": "Je ne mange que des aliments halal",
    "intake.faith.q_ramadan": "Je jeûne chaque Ramadan",
    "intake.faith.q_halalIncome": "Je fais un grand effort pour maintenir un revenu halal",
    "intake.faith.q_childrenReligious": "Il est important que mes enfants soient élevés dans la religion",

    // PLACEHOLDERS / ERRORS
    "intake.placeholders.firstName": "ex. Sarah",
    "intake.placeholders.age": "ex. 28",
    "intake.placeholders.cityShort": "ex. Toronto, ON",
    "intake.placeholders.cityLong": "ex. Toronto, ON ou Montréal, QC",
    "intake.placeholders.otherLanguages": "ex. espagnol, allemand",
    "intake.placeholders.email": "ex. sara@gmail.com",
    "intake.placeholders.phone": "ex. +1 416 555 0123",

    "intake.errors.age18": "Vous devez avoir au moins 18 ans",
    "intake.errors.emailInvalid": "Veuillez entrer une adresse e-mail valide.",

    "intake.sections.openness": "Ouverture et logistique",
    "intake.sections.preferences": "Préférences",
    "intake.sections.introductions": "Présentations",
    "intake.sections.spousePreferences": "Préférences du conjoint",
    "intake.sections.contact": "Coordonnées",
    "intake.sections.finalReflection": "Réflexion finale — Votre intention",

    "intake.preferences.ageOpenTo": "À quelle(s) tranche(s) d’âge êtes-vous ouvert(e) ?",
    "intake.preferences.agePreferred": "Tranche(s) d’âge préférée(s) :",

    "intake.introductions.q1": "Êtes-vous ouvert(e) aux présentations par des tiers (ex. : par le parent/tuteur/ami(e) de la personne) ?",
    "intake.introductions.q2": "Si oui, avec qui êtes-vous à l’aise ?",

    "intake.spouse.introTitle": "Quelle importance accordez-vous aux éléments suivants chez un(e) partenaire ?",
    "intake.spouse.introBody": "Pour chaque affirmation, indiquez son niveau d’importance chez un(e) partenaire. Vos réponses nous aident à trouver la meilleure compatibilité globale.",
    "intake.spouse.q_faith_role": "Un engagement fort envers la pratique religieuse quotidienne",
    "intake.spouse.q_faith_ramadan": "Jeûner régulièrement pendant le Ramadan",
    "intake.spouse.q_faith_halal_food": "Consommer strictement de la nourriture halal",
    "intake.spouse.q_faith_halal_income": "Gagner un revenu halal",
    "intake.spouse.q_fam_inlaws": "Être à l’aise dans un foyer familial (ex. : près ou avec les parents)",
    "intake.spouse.q_fam_parents": "Impliquer les parents dans les décisions importantes de la vie",
    "intake.spouse.q_fam_finances": "Fonder une famille seulement après une stabilité financière",
    "intake.spouse.q_rel_conflict": "Aborder les conflits rapidement plutôt que les repousser",
    "intake.spouse.q_rel_openness": "Être à l’aise pour discuter ouvertement de sujets difficiles",
    "intake.spouse.q_rel_processing": "Prendre le temps de réfléchir à ses émotions avant d’en discuter",
    "intake.spouse.q_lifestyle_travel": "Aimer voyager et découvrir de nouvelles expériences",
    "intake.spouse.q_lifestyle_exercise": "Accorder la priorité à la santé et à l’exercice",

    "intake.contact.title": "Comment pouvons-nous vous contacter ?",
    "intake.contact.email": "Adresse e-mail",
    "intake.contact.phone": "Numéro de téléphone (facultatif)",
    "intake.contact.helper": "Nous utiliserons votre adresse e-mail pour confirmer votre enregistrement et vous envoyer les détails de correspondance, et elle ne sera partagée qu’avec votre correspondant.",

    // PHOTO STEP
    "intake.sections.photo": "Presque terminé",

    "intake.photo.title": "Ajouter une photo ?",
    "intake.photo.descriptionBeforeEmphasis": "C’est totalement facultatif. Si vous en ajoutez une, elle sera",
    "intake.photo.descriptionEmphasis": "uniquement",
    "intake.photo.descriptionAfterEmphasis": "visible par la personne avec qui vous serez jumelé(e) — jamais publiquement, ni à qui que ce soit d’autre.",
    "intake.photo.previewAlt": "Votre photo",
    "intake.photo.remove": "Supprimer",
    "intake.photo.tapToUpload": "Appuyez pour téléverser",
    "intake.photo.privacyNote": "Privé et chiffré — visible uniquement par votre match.",
    "intake.photo.helper": "Vous pouvez passer cette étape et en ajouter une plus tard.",

    "intake.reflection.labelCandidate": "Une chose importante à comprendre à mon sujet (mes valeurs, mes objectifs ou ma vision du mariage) est…",
    "intake.reflection.labelGuardian": "De votre point de vue, qu’est-ce qui compte le plus dans un(e) partenaire ?",
    "intake.reflection.placeholder": "Partagez ce qui vous vient à l’esprit...",
    "intake.reflection.helper": "Ceci est facultatif, mais nous aide à mieux vous comprendre.",

    "intake.openness.abroad": "Il est important que mes enfants passent du temps à vivre à l’étranger",
    "intake.openness.childrenLang": "Mes enfants devraient apprendre plus d’une langue",
    "intake.openness.longDistance": "Je suis ouvert(e) à une relation à distance",
    "intake.openness.financialRisk": "Je suis à l’aise avec la prise de risques financiers",
    "intake.openness.financialSupport": "Je serais à l’aise d’être soutenu(e) financièrement par mon/ma partenaire",

    // WELCOME STEP
    "welcome.title.prefix": "Commençons par",
    "welcome.title.highlight": "l’intention.",
    "welcome.title.suffix": "",
    "welcome.body.line1": "Cela prend environ 5 à 7 minutes.",
    "welcome.body.line2": "Il n’y a pas de bonnes ou de mauvaises réponses.",
    "welcome.body.line3a": "La correspondance est basée sur l’alignement —",
    "welcome.body.line3b": "pas sur la perfection.",
    "welcome.cta": "Continuer",

    // AGE PREFERENCES STEP
    "agePrefs.helper": "Sélectionnez toutes les tranches d’âge qui vous conviennent. Cela nous aide à vous proposer des profils compatibles.",

    // BASICS STEP
    "basics.helper": "Parlez-nous un peu de vous.",
    "basics.firstName": "Prénom",
    "basics.firstName.placeholder": "ex. Sarah",
    "basics.age": "Âge",
    "basics.age.placeholder": "ex. 28",
    "basics.gender": "Genre",
    "basics.city": "Ville/Région",
    "basics.city.placeholder": "ex. Toronto, ON ou Montréal, QC",
    "basics.inPerson": "Ouvert(e) aux événements en personne ?",
    "gender.male": "Homme",
    "gender.female": "Femme",
    "validation.age18": "Vous devez avoir au moins 18 ans",

    // COMPLETION STEP
    "completion.title": "C'est fait.",
    "completion.body": "Votre profil a été créé.",
    "completion.cta": "Continuer",

    // SUCCESS STEP
    "success.title": "Tout est prêt.",
    "success.body": "Votre place est confirmée. Nous vous enverrons un e-mail lorsque le processus de mise en relation commencera.",
    "success.card.planLabel": "Forfait",
    "success.card.statusLabel": "Statut",
    "success.card.trialEndsLabel": "Fin de l’essai",
    "success.cta": "Aller à l’accueil",
    "success.footer": "Consultez votre e-mail pour les prochaines étapes et les mises à jour.",

    // CANCEL STEP
    "cancel.title": "Aucun souci.",
    "cancel.body": "Votre progression est enregistrée. Vous pouvez revenir à tout moment pour finaliser votre paiement.",
    "cancel.ctaReturn": "Retourner au paiement",
    "cancel.ctaHome": "Aller à l’accueil",
    "cancel.footer": "Nous avons enregistré vos réponses. Reprenez exactement là où vous vous êtes arrêté(e).",

    // FAITH VALUES STEP
    "steps.faith.title": "Foi et valeurs",
    "steps.faith.subtitle": "Chacun pratique différemment. Répondez honnêtement.",

    "intake.faith.role": "La religion joue un rôle important dans ma vie",
    "intake.faith.children": "Il est important que mes enfants soient élevés dans la religion",
    "intake.faith.halalFood": "Je ne mange que des aliments halal",
    "intake.faith.ramadan": "Je jeûne chaque Ramadan",
    "intake.faith.halalIncome": "Je fais de grands efforts pour maintenir un revenu halal",

    // LANGUAGE STEP
    "steps.language.title": "Langue et communication",
    "steps.language.subtitle": "Cela nous aide à éviter les malentendus liés à la communication.",
    "steps.language.spoken": "Langues dans lesquelles vous êtes à l’aise pour communiquer en couple",
    "steps.language.other": "Autres langues (optionnel)",
    "steps.language.otherPlaceholder": "ex. espagnol, allemand",
    "steps.language.preferred": "Langue(s) que vous préféreriez utiliser avec un(e) partenaire (optionnel)",

    // LIFESTYLE STEP
    "steps.lifestyle.title": "Style de vie et énergie",
    "steps.lifestyle.subtitle": "Comment aimez-vous passer votre temps ?",

    "intake.lifestyle.activity.q": "Si je ne fais rien pendant toute une journée, je me sens…",
    "intake.lifestyle.activity.min": "Agité(e)",
    "intake.lifestyle.activity.max": "Parfaitement bien",

    "intake.lifestyle.travel.q": "Voyager me rend anxieux(se)",

    "intake.lifestyle.vacation.q": "Quand je pars en vacances, je préfère…",
    "intake.lifestyle.vacation.min": "Me détendre|toute la journée",
    "intake.lifestyle.vacation.max": "Un programme|bien rempli",

    "intake.lifestyle.exercise.q": "L’exercice est une partie importante de mon mode de vie",
    "intake.lifestyle.diet.q": "Je fais attention à ce que je mange",

    // MARRIAGE FAMILY STEP
    "steps.marriageFamily.title": "Mariage et famille",
    "steps.marriageFamily.subtitle": "Votre vision de l’avenir.",

    "intake.marriageAge.q": "Se marier jeune est une erreur",
    "intake.childrenCount.label": "Je souhaite une famille avec… (nombre d’enfants)",
    "intake.famFinances.q": "Les finances ne devraient pas retarder la création d’une famille",
    "intake.famInlaws.q": "Cela ne me dérange pas que la famille de mon/ma conjoint(e) vive avec nous",
    "intake.famParentsDecisions.q": "Je consulte mes parents pour les décisions importantes",

    // OPENNESS STEP
    "steps.openness.title": "Ouverture et aspects pratiques",
    "steps.openness.subtitle": "Considérations pratiques pour votre avenir.",

      // --- PAYMENT ---
    "payment.title": "Choisissez votre niveau d’engagement",
    "payment.subtitle": "Vous faites désormais partie de Niyyah. Sélectionnez l’option qui correspond à votre rythme.",

    "payment.trial.title": "Chaque forfait commence par un essai gratuit de 60 jours.",
    "payment.trial.subtitle": "Vous ne serez pas facturé(e) avant la fin. Annulez à tout moment.",

    "payment.error.missingEmail": "E-mail manquant. Veuillez revenir en arrière et saisir votre adresse e-mail.",
    "payment.error.failed": "Le paiement a échoué. Veuillez réessayer.",
    "payment.backToIntake": "Retour au formulaire",

    "payment.mostPopular": "Le plus populaire",
    "payment.redirecting": "Redirection...",

    "payment.badge.private": "Privé. Aucun profil public.",
    "payment.badge.flexible": "Vous pouvez changer d’offre ou annuler à tout moment.",
    "payment.footer": "La mise en relation est basée sur l’alignement et la disponibilité. La qualité avant la quantité.",

    // Plan: Community
    "payment.plan.community.title": "Inscription de base",
    "payment.plan.community.price": "10 $",
    "payment.plan.community.period": "par mois (après l’essai gratuit)",
    "payment.plan.community.cta": "S’inscrire maintenant",
    "payment.plan.community.f1": "Rejoindre la communauté Niyyah",
    "payment.plan.community.f2": "Possibilité d’être invité(e) à des événements exclusifs",
    "payment.plan.community.f3": "Admissibilité aux futures mises en relation",
    "payment.plan.community.f4": "Profil privé basé sur les valeurs",

    // Plan: Matching
    "payment.plan.matching.title": "Mise en relation active",
    "payment.plan.matching.price": "20 $",
    "payment.plan.matching.period": "par mois (après l’essai gratuit)",
    "payment.plan.matching.cta": "Activer la mise en relation",
    "payment.plan.matching.f1": "Tout ce qui est inclus dans l’inscription de base",
    "payment.plan.matching.f2": "Considération continue pour des mises en relation privées",
    "payment.plan.matching.f3": "Présentations périodiques de profils compatibles",
    "payment.plan.matching.f4": "Priorité par rapport aux membres passifs",

    // --- LEGACY STEPS ---
    "legacy.reflection.title": "Réflexion finale",
    "legacy.reflection.helper": "C’est facultatif, mais cela nous aide à mieux vous comprendre.",
    "legacy.reflection.label": "Une chose importante à comprendre à mon sujet est…",
    "legacy.reflection.placeholder": "Partagez ce qui vous vient à l’esprit...",

    "legacy.relationship.title": "Style relationnel",
    "legacy.relationship.subtitle": "Comment vous reliez-vous aux autres ?",
    "legacy.relationship.q_romance": "J’apprécie les grands gestes romantiques",
    "legacy.relationship.q_conflict": "Je préfère résoudre les conflits immédiatement plutôt que d’attendre",
    "legacy.relationship.q_processing": "J’ai besoin de temps pour réfléchir avant de partager ce qui me dérange",
    "legacy.relationship.q_similarity": "Je souhaite que mon/ma partenaire me ressemble",
    "legacy.relationship.q_openness": "Je suis à l’aise pour discuter ouvertement de sujets difficiles",

    "checkoutSuccess.title": "Vous êtes inscrit.",
    "checkoutSuccess.subtitleLine1": "Vous faites désormais partie de la version bêta.",
    "checkoutSuccess.subtitleLine2": "Nous vous enverrons un e-mail lorsqu'il y aura une correspondance.",

    "checkoutSuccess.statusLabel": "Statut",
    "checkoutSuccess.statusValue": "Accès bêta",

    "checkoutSuccess.nextLabel": "Prochaine étape",
    "checkoutSuccess.nextBody": "Nous vous informerons par e-mail lorsque nous trouverons une correspondance potentielle.",

    "checkoutSuccess.inviteTitle": "Niyyah grandit mieux au sein de cercles de confiance.",
    "checkoutSuccess.inviteBody": "Si vous connaissez quelqu’un qui cherche le mariage pour lui/elle-même — ou pour quelqu’un d’autre — veuillez l’inviter en privé.",

    "checkoutSuccess.shareText": "Une façon réfléchie et privée de se rencontrer.",
    "checkoutSuccess.share": "Partager",
    "checkoutSuccess.copyLink": "Copier le lien",
    "checkoutSuccess.copied": "Copié ✓",

    "checkoutSuccess.backHome": "Retour à l’accueil",

    "checkoutSuccess.footer": "Consultez votre e-mail pour la confirmation et les prochaines étapes.",
    "checkoutSuccess.sessionLabel": "Session",

  },

  ar: {

    //INTAKE ONLY
    "common.next": "التالي",
    "common.back": "رجوع",
    "common.strongly": "بشدة",
    "common.agree": "أوافق",
    "common.disagree": "لا أوافق",
    "common.stronglyDisagree": "أعارض|بشدة",
    "common.stronglyAgree": "أوافق|بشدة",
    "common.selectAll": "اختر كل ما ينطبق.",
    "common.notImportant": "غير مهم",
    "common.veryImportant": "مهم جدًا",
    "common.yes": "نعم",
    "common.no": "لا",
    "common.male": "ذكر",
    "common.female": "أنثى",

    "inPerson.yes": "نعم",
    "inPerson.maybe": "ربما",
    "inPerson.notNow": "ليس الآن",

    "lang.en": "الإنجليزية",
    "lang.fr": "الفرنسية",
    "lang.ar": "العربية",
    "lang.ur": "الأردية",

    // SECTIONS
    "intake.sections.gettingStarted": "البدء",
    "intake.sections.whoYoureHelping": "الشخص الذي تساعده",
    "intake.sections.basics": "المعلومات الأساسية",
    "intake.sections.language": "اللغة والتواصل",
    "intake.sections.lifestyle": "نمط الحياة والطاقة",
    "intake.sections.relationship": "أسلوب العلاقة",
    "intake.sections.family": "الزواج والعائلة",
    "intake.sections.faith": "الدين والقيم",

    // GETTING STARTED
    "intake.gettingStarted.q_forWho": "لمن هذه التجربة؟",
    "intake.gettingStarted.forMe": "لي",
    "intake.gettingStarted.forSomeoneElse": "أساعد شخصًا آخر في العثور على شريك",
    "intake.gettingStarted.q_role": "ما هو دورك؟",
    "intake.gettingStarted.guardianNote": "سيوافق طالب الزواج على كل ترشيح.",

    // ROLES
    "intake.roles.parent": "والد/والدة",
    "intake.roles.wali": "ولي",
    "intake.roles.sibling": "أخ/أخت",
    "intake.roles.friend": "صديق/صديقة",
    "intake.roles.other": "أخرى",

    // WHO YOU’RE HELPING
    "intake.whoHelping.firstName": "ما اسمه/اسمها الأول؟",
    "intake.whoHelping.age": "كم عمره/عمرها؟",
    "intake.whoHelping.gender": "ما جنسه/جنسها؟",
    "intake.whoHelping.location": "أين يقيم/تقيم؟",

    // BASICS
    "intake.basics.firstName": "ما اسمك الأول؟",
    "intake.basics.age": "كم عمرك؟",
    "intake.basics.gender": "ما جنسك؟",
    "intake.basics.location": "أين تقيم؟",
    "intake.basics.inPerson": "هل أنت منفتح/ة على الفعاليات الحضورية؟",

    // LANGUAGE
    "intake.language.guardianLabel": "أبحث عن شخص يتحدث…",
    "intake.language.spokenLabel": "ما اللغات التي تشعر بالراحة في استخدامها ضمن علاقة؟",
    "intake.language.preferredLabel": "ما اللغة (أو اللغات) التي تفضل استخدامها مع الشريك؟ (اختياري)",
    "intake.language.other": "لغات أخرى (اختياري)",
    "intake.language.helper": "يساعدنا ذلك على تجنب سوء التفاهم في التواصل.",

    // LIFESTYLE
    "intake.lifestyle.q_activity": "إذا لم أفعل شيئًا طوال اليوم، أشعر بـ…",
    "intake.lifestyle.min_restless": "توتر وعدم راحة",
    "intake.lifestyle.max_content": "رضا تام",
    "intake.lifestyle.q_travelAnxious": "السفر يسبب لي القلق",
    "intake.lifestyle.q_vacation": "عندما أذهب في إجازة، أفضل…",
    "intake.lifestyle.min_relaxing": "الاسترخاء|طوال اليوم",
    "intake.lifestyle.max_fullSchedule": "جدولًا|مليئًا بالأنشطة",
    "intake.lifestyle.q_exercise": "ممارسة الرياضة جزء مهم من نمط حياتي",
    "intake.lifestyle.q_diet": "أهتم بما أتناوله من طعام",

    // RELATIONSHIP
    "intake.relationship.q_romance": "أستمتع بالمبادرات الرومانسية الكبيرة",
    "intake.relationship.q_conflict": "أفضل حل الخلاف فورًا بدلًا من الانتظار",
    "intake.relationship.q_processing": "أحتاج إلى وقت للتفكير قبل مشاركة ما يزعجني",
    "intake.relationship.q_similarity": "أرغب أن يكون شريكي مشابهًا لي",
    "intake.relationship.q_openness": "أشعر بالراحة عند مناقشة المواضيع الصعبة بصراحة",

    // FAMILY
    "intake.family.q_marriageYoung": "الزواج في سن مبكرة خطأ",
    "intake.family.q_prevMarried_guardian": "هل سبق للشخص الذي تساعده أن تزوج؟",
    "intake.family.q_prevMarried_candidate": "هل سبق لك الزواج؟",
    "intake.family.q_hasChildren": "هل لديك أطفال حاليًا؟",
    "intake.family.q_childrenLivingWith": "هل يعيش أطفالك معك؟",
    "intake.family.someOfTheTime": "بعض الوقت",
    "intake.family.q_openMoreChildren": "هل أنت منفتح/ة على إنجاب المزيد من الأطفال؟",
    "intake.family.unsure": "غير متأكد/ة",
    "intake.family.q_childrenCount": "كم عدد الأطفال الذين ترغب بهم؟",
    "intake.family.q_finances": "لا ينبغي أن تؤخر الأمور المالية بدء تكوين عائلة",
    "intake.family.q_inlaws": "لا بأس إذا عاشت عائلة شريكي/شريكتي معنا",
    "intake.family.q_parentsDecisions": "أستشير والديّ في القرارات المهمة",

    // FAITH
    "intake.faith.q_role": "يلعب الدين دورًا مهمًا في حياتي",
    "intake.faith.q_denomImportance": "من المهم بالنسبة لي أن نتشارك نفس المذهب الإسلامي (مثل سني، شيعي، إلخ)",
    "intake.faith.q_denomWhich": "إلى أي مذهب تنتمي؟ (اختياري)",
    "intake.faith.denom.none": "لا أنتمي إلى مذهب محدد",
    "intake.faith.denom.sunni": "سني",
    "intake.faith.denom.shia": "شيعي",
    "intake.faith.denom.sufi": "صوفي",
    "intake.faith.denom.salafi": "سلفي",
    "intake.faith.denom.preferNot": "أفضل عدم التحديد",
    "intake.faith.denom.other": "أخرى",
    "intake.faith.denom.otherLabel": "مذهب آخر (اختياري)",
    "intake.faith.denom.otherPlaceholder": "اكتب هنا…",
    "intake.faith.q_halalFood": "لا أتناول إلا الطعام الحلال",
    "intake.faith.q_ramadan": "أصوم كل رمضان",
    "intake.faith.q_halalIncome": "أبذل جهدًا كبيرًا للحفاظ على دخل حلال",
    "intake.faith.q_childrenReligious": "من المهم أن تتم تربية أطفالي على الدين",

    // PLACEHOLDERS / ERRORS
    "intake.placeholders.firstName": "مثال: سارة",
    "intake.placeholders.age": "مثال: 28",
    "intake.placeholders.cityShort": "مثال: تورونتو، أونتاريو",
    "intake.placeholders.cityLong": "مثال: تورونتو، أونتاريو أو مونتريال، كيبيك",
    "intake.placeholders.otherLanguages": "مثال: الإسبانية، الألمانية",
    "intake.placeholders.email": "مثال: sara@gmail.com",
    "intake.placeholders.phone": "مثال: ‎+1 416 555 0123",

    "intake.errors.age18": "يجب أن يكون عمرك 18 عامًا أو أكثر",
    "intake.errors.emailInvalid": "يرجى إدخال بريد إلكتروني صالح.",

    "intake.sections.openness": "الانفتاح واللوجستيات",
    "intake.sections.preferences": "التفضيلات",
    "intake.sections.introductions": "التعارف",
    "intake.sections.spousePreferences": "تفضيلات الشريك",
    "intake.sections.contact": "معلومات التواصل",
    "intake.sections.finalReflection": "التأمل الأخير — نيتك",

    "intake.preferences.ageOpenTo": "ما الفئة (أو الفئات) العمرية التي أنت منفتح عليها؟",
    "intake.preferences.agePreferred": "الفئة (أو الفئات) العمرية المفضلة:",

    "intake.introductions.q1": "هل أنت منفتح على التعارف من خلال طرف ثالث (مثل والد/ولي/صديق الطرف الآخر)؟",
    "intake.introductions.q2": "إذا كانت الإجابة نعم، مع من تشعر بالارتياح؟",

    "intake.spouse.introTitle": "ما مدى أهمية الأمور التالية في شريك الحياة؟",
    "intake.spouse.introBody": "لكل عبارة، قيّم مدى أهميتها في شريك الحياة. تساعدنا إجاباتك في إيجاد أفضل توافق ممكن.",
    "intake.spouse.q_faith_role": "الالتزام القوي بالممارسة الدينية اليومية",
    "intake.spouse.q_faith_ramadan": "الصيام بانتظام خلال رمضان",
    "intake.spouse.q_faith_halal_food": "الالتزام الصارم بتناول الطعام الحلال",
    "intake.spouse.q_faith_halal_income": "كسب دخل حلال",
    "intake.spouse.q_fam_inlaws": "الارتياح للعيش في بيئة عائلية (مثل القرب من الوالدين أو العيش معهما)",
    "intake.spouse.q_fam_parents": "إشراك الوالدين في القرارات المصيرية",
    "intake.spouse.q_fam_finances": "تكوين أسرة فقط بعد الاستقرار المالي",
    "intake.spouse.q_rel_conflict": "معالجة الخلافات في وقت مبكر بدل تأجيلها",
    "intake.spouse.q_rel_openness": "الراحة في مناقشة المواضيع الصعبة بصراحة",
    "intake.spouse.q_rel_processing": "أخذ وقت لمعالجة المشاعر قبل مناقشتها",
    "intake.spouse.q_lifestyle_travel": "حب السفر وخوض تجارب جديدة",
    "intake.spouse.q_lifestyle_exercise": "إعطاء الأولوية للصحة وممارسة الرياضة",

    "intake.contact.title": "كيف يمكننا التواصل معك؟",
    "intake.contact.email": "البريد الإلكتروني",
    "intake.contact.phone": "رقم الهاتف (اختياري)",
    "intake.contact.helper": "سنستخدم بريدك الإلكتروني لتأكيد تسجيلك وإرسال تفاصيل المطابقة إليك، ولن تتم مشاركته إلا مع الطرف المطابق لك.",

    // PHOTO STEP
    "intake.sections.photo": "على وشك الانتهاء",

    "intake.photo.title": "إضافة صورة؟",
    "intake.photo.descriptionBeforeEmphasis": "هذا اختياري تمامًا. إذا أضفت صورة، فسيتم",
    "intake.photo.descriptionEmphasis": "عرضها فقط",
    "intake.photo.descriptionAfterEmphasis": "على الشخص الذي يتم مطابقتك معه — ولن تُعرض علنًا أو لأي شخص آخر.",
    "intake.photo.previewAlt": "صورتك",
    "intake.photo.remove": "إزالة",
    "intake.photo.tapToUpload": "اضغط للتحميل",
    "intake.photo.privacyNote": "خاص ومشفّر — مرئي فقط للشخص المطابق لك.",
    "intake.photo.helper": "يمكنك تخطي هذه الخطوة وإضافة صورة لاحقًا.",

    "intake.reflection.labelCandidate": "أمر مهم يجب أن يفهمه الآخر عني (قيمِي، أهدافي، أو رؤيتي للزواج) هو…",
    "intake.reflection.labelGuardian": "من وجهة نظرك، ما الأهم في الشريك المناسب؟",
    "intake.reflection.placeholder": "شارك ما يخطر ببالك...",
    "intake.reflection.helper": "هذا اختياري، لكنه يساعدنا على فهمك بشكل أفضل.",

    "intake.openness.abroad": "من المهم أن يقضي أطفالي وقتًا في العيش في الخارج",
    "intake.openness.childrenLang": "يجب أن يتعلم أطفالي أكثر من لغة واحدة",
    "intake.openness.longDistance": "أنا منفتح/منفتحة على علاقة عن بُعد",
    "intake.openness.financialRisk": "أشعر بالراحة تجاه المخاطر المالية",
    "intake.openness.financialSupport": "سأكون مرتاحًا/مرتاحـة لتلقي دعم مالي من شريكي",

    // WELCOME STEP
    "welcome.title.prefix": "لنبدأ بـ",
    "welcome.title.highlight": "النية.",
    "welcome.title.suffix": "",
    "welcome.body.line1": "يستغرق هذا حوالي 5 إلى 7 دقائق.",
    "welcome.body.line2": "لا توجد إجابات صحيحة أو خاطئة.",
    "welcome.body.line3a": "المطابقة تعتمد على التوافق —",
    "welcome.body.line3b": "وليس الكمال.",
    "welcome.cta": "متابعة",

    // AGE PREFERENCES STEP
     "agePrefs.helper": "حدد جميع الفئات العمرية التي تناسبك. يساعدنا ذلك في عرض تطابقات متوافقة معك.",

    // BASICS STEP
    "basics.helper": "أخبرنا قليلاً عن نفسك.",
    "basics.firstName": "الاسم الأول",
    "basics.firstName.placeholder": "مثال: سارة",
    "basics.age": "العمر",
    "basics.age.placeholder": "مثال: 28",
    "basics.gender": "الجنس",
    "basics.city": "المدينة/المنطقة",
    "basics.city.placeholder": "مثال: تورونتو، أونتاريو أو مونتريال، كيبيك",
    "basics.inPerson": "هل أنت منفتح/ة على الفعاليات الحضورية؟",
    "gender.male": "ذكر",
    "gender.female": "أنثى",
    "validation.age18": "يجب أن يكون عمرك 18 عامًا أو أكثر",

    // COMPLETION STEP
    "completion.title": "تمّ بنجاح.",
    "completion.body": "تم إنشاء ملفك الشخصي.",
    "completion.cta": "متابعة",

    // SUCCESS STEP
    "success.title": "أنت جاهز تمامًا.",
    "success.body": "تم تأكيد حجزك. سنرسل لك بريدًا إلكترونيًا عند بدء عملية المطابقة.",
    "success.card.planLabel": "الخطة",
    "success.card.statusLabel": "الحالة",
    "success.card.trialEndsLabel": "ينتهي الإصدار التجريبي",
    "success.cta": "الانتقال إلى الصفحة الرئيسية",
    "success.footer": "تحقق من بريدك الإلكتروني للاطلاع على الخطوات التالية والتحديثات.",

    // CANCEL STEP
    "cancel.title": "لا داعي للقلق.",
    "cancel.body": "تم حفظ تقدمك. يمكنك العودة في أي وقت لإكمال عملية الدفع.",
    "cancel.ctaReturn": "العودة إلى الدفع",
    "cancel.ctaHome": "الانتقال إلى الصفحة الرئيسية",
    "cancel.footer": "لقد حفظنا إجاباتك. تابع من حيث توقفت.",

    // FAITH VALUES STEP
    "steps.faith.title": "الدين والقيم",
    "steps.faith.subtitle": "لكل شخص طريقته في الممارسة. أجب بصدق.",

    "intake.faith.role": "يلعب الدين دورًا مهمًا في حياتي",
    "intake.faith.children": "من المهم أن تتم تربية أطفالي على الدين",
    "intake.faith.halalFood": "لا أتناول إلا الطعام الحلال",
    "intake.faith.ramadan": "أصوم كل رمضان",
    "intake.faith.halalIncome": "أبذل جهدًا كبيرًا للحفاظ على دخل حلال",

    // LANGUAGE STEP
    "steps.language.title": "اللغة والتواصل",
    "steps.language.subtitle": "يساعدنا ذلك على تجنب سوء التفاهم في التواصل.",
    "steps.language.spoken": "اللغات التي تشعر بالراحة في استخدامها ضمن علاقة",
    "steps.language.other": "لغات أخرى (اختياري)",
    "steps.language.otherPlaceholder": "مثال: الإسبانية، الألمانية",
    "steps.language.preferred": "اللغة (أو اللغات) التي تفضل استخدامها مع الشريك (اختياري)",

    // LIFESTYLE STEP
    "steps.lifestyle.title": "نمط الحياة والطاقة",
    "steps.lifestyle.subtitle": "كيف تفضل قضاء وقتك؟",

    "intake.lifestyle.activity.q": "إذا لم أفعل شيئًا طوال اليوم، أشعر بـ…",
    "intake.lifestyle.activity.min": "توتر وعدم راحة",
    "intake.lifestyle.activity.max": "رضا تام",

    "intake.lifestyle.travel.q": "السفر يسبب لي القلق",

    "intake.lifestyle.vacation.q": "عندما أذهب في إجازة، أفضل…",
    "intake.lifestyle.vacation.min": "الاسترخاء|طوال اليوم",
    "intake.lifestyle.vacation.max": "جدولًا|مليئًا بالأنشطة",

    "intake.lifestyle.exercise.q": "ممارسة الرياضة جزء مهم من نمط حياتي",
    "intake.lifestyle.diet.q": "أهتم بما أتناوله من طعام",

    // MARRIAGE FAMILY STEP
    "steps.marriageFamily.title": "الزواج والعائلة",
    "steps.marriageFamily.subtitle": "رؤيتك للمستقبل.",

    "intake.marriageAge.q": "الزواج في سن مبكرة خطأ",
    "intake.childrenCount.label": "أرغب في تكوين عائلة تضم… (عدد الأطفال)",
    "intake.famFinances.q": "لا ينبغي أن تؤخر الأمور المالية بدء تكوين عائلة",
    "intake.famInlaws.q": "لا بأس إذا عاشت عائلة شريكي/شريكتي معنا",
    "intake.famParentsDecisions.q": "أستشير والديّ في القرارات المهمة",

    // OPENNESS STEP
    "steps.openness.title": "الانفتاح والجوانب العملية",
    "steps.openness.subtitle": "اعتبارات عملية لمستقبلك.",

     // --- PAYMENT ---
    "payment.title": "اختر مستوى التزامك",
    "payment.subtitle": "أصبحت الآن جزءًا من Niyyah. اختر الخيار الذي يناسب وتيرتك.",

    "payment.trial.title": "تبدأ كل خطة بفترة تجريبية مجانية لمدة 60 يومًا.",
    "payment.trial.subtitle": "لن يتم تحصيل أي رسوم حتى تنتهي الفترة. يمكنك الإلغاء في أي وقت.",

    "payment.error.missingEmail": "البريد الإلكتروني مفقود. يرجى العودة وإدخال بريدك الإلكتروني.",
    "payment.error.failed": "فشلت عملية الدفع. يرجى المحاولة مرة أخرى.",
    "payment.backToIntake": "العودة إلى النموذج",

    "payment.mostPopular": "الأكثر شيوعًا",
    "payment.redirecting": "جارٍ التحويل...",

    "payment.badge.private": "خاص. لا توجد ملفات شخصية عامة.",
    "payment.badge.flexible": "يمكنك الترقية أو الإلغاء في أي وقت.",
    "payment.footer": "تعتمد المطابقة على التوافق والتوفر. الجودة أهم من الكمية.",

    // Plan: Community
    "payment.plan.community.title": "الاشتراك الأساسي",
    "payment.plan.community.price": "10$",
    "payment.plan.community.period": "شهريًا (بعد الفترة التجريبية المجانية)",
    "payment.plan.community.cta": "اشترك الآن",
    "payment.plan.community.f1": "الانضمام إلى مجتمع Niyyah",
    "payment.plan.community.f2": "إمكانية الدعوة إلى فعاليات خاصة",
    "payment.plan.community.f3": "الأهلية للمطابقة مستقبلاً",
    "payment.plan.community.f4": "ملف شخصي خاص قائم على القيم",

    // Plan: Matching
    "payment.plan.matching.title": "المطابقة النشطة",
    "payment.plan.matching.price": "20$",
    "payment.plan.matching.period": "شهريًا (بعد الفترة التجريبية المجانية)",
    "payment.plan.matching.cta": "تفعيل المطابقة",
    "payment.plan.matching.f1": "كل ما يتضمنه الاشتراك الأساسي",
    "payment.plan.matching.f2": "النظر المستمر في المطابقات الخاصة",
    "payment.plan.matching.f3": "تقديم ترشيحات دورية",
    "payment.plan.matching.f4": "أولوية على الأعضاء غير النشطين",

    // --- LEGACY STEPS ---
    "legacy.reflection.title": "تأمل أخير",
    "legacy.reflection.helper": "هذا اختياري، لكنه يساعدنا على فهمك بشكل أفضل.",
    "legacy.reflection.label": "أمر مهم أود أن يفهمه الآخر عني هو…",
    "legacy.reflection.placeholder": "شارك ما يخطر ببالك...",

    "legacy.relationship.title": "أسلوب العلاقة",
    "legacy.relationship.subtitle": "كيف تتعامل مع الآخرين؟",
    "legacy.relationship.q_romance": "أستمتع بالمبادرات الرومانسية الكبيرة",
    "legacy.relationship.q_conflict": "أفضل حل الخلاف فورًا بدلًا من الانتظار",
    "legacy.relationship.q_processing": "أحتاج إلى وقت للتفكير قبل مشاركة ما يزعجني",
    "legacy.relationship.q_similarity": "أرغب أن يكون شريكي مشابهًا لي",
    "legacy.relationship.q_openness": "أشعر بالراحة عند مناقشة المواضيع الصعبة بصراحة",

    // CHECKOUT SUCCESS PAGE
    "checkoutSuccess.title": "أنت الآن ضمن المنصة.",
    "checkoutSuccess.subtitleLine1": "أصبحت الآن جزءًا من النسخة التجريبية.",
    "checkoutSuccess.subtitleLine2": "سنرسل لك بريدًا إلكترونيًا عند توفر تطابق.",

    "checkoutSuccess.statusLabel": "الحالة",
    "checkoutSuccess.statusValue": "وصول تجريبي",

    "checkoutSuccess.nextLabel": "ما الخطوة التالية؟",
    "checkoutSuccess.nextBody": "سنخطرك عبر البريد الإلكتروني عندما نجد تطابقًا محتملاً.",

    "checkoutSuccess.inviteTitle": "تنمو Niyyah بشكل أفضل من خلال دوائر الثقة.",
    "checkoutSuccess.inviteBody": "إذا كنت تعرف شخصًا يبحث عن الزواج لنفسه — أو نيابةً عن شخص آخر — فيرجى دعوته بشكل خاص.",

    "checkoutSuccess.shareText": "طريقة مدروسة وخاصة للتعارف.",
    "checkoutSuccess.share": "مشاركة",
    "checkoutSuccess.copyLink": "نسخ الرابط",
    "checkoutSuccess.copied": "تم النسخ ✓",

    "checkoutSuccess.backHome": "العودة إلى الصفحة الرئيسية",

    "checkoutSuccess.footer": "تحقق من بريدك الإلكتروني للحصول على التأكيد والخطوات التالية.",
    "checkoutSuccess.sessionLabel": "الجلسة",

  },
};